import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  type ElementRef,
  inject,
  Injector,
  input,
  LOCALE_ID,
  output,
  runInInjectionContext,
  viewChild,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

import { ECElementEvent } from 'echarts';
import type { ECharts } from 'echarts/core';
import type { EChartsOption } from 'echarts/types/dist/shared';
import {
  animationFrameScheduler,
  buffer,
  combineLatest,
  fromEvent,
  merge,
  pairwise,
  ReplaySubject,
} from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  observeOn,
  share,
  shareReplay,
  skip,
  take,
} from 'rxjs/operators';

import { ReportsEChartsOption, injectReportsChartTheme } from '@core/reports-types';
import { ResizeObserverEntryDirective, WindowRef } from '@core/user-interface-legacy';
import { injectUntilDestroyed } from '@core/utils-legacy';

import { getDefaultTooltipPosition } from '../../visualizations/strategies/composables/tooltip';
import { init } from '../../visualizations/strategies/echarts';
import {
  getChartThemeInitOptions,
  getChartThemeStylingOptions,
} from '../../visualizations/strategies/theme';
import { ChartMomentConfigService } from '../legacy-chart/chart-moment-config.service';

type EchartsEventName = Parameters<ECharts['on']>[0];

export type ReportsChartEventBindings = Record<EchartsEventName, (event: ECElementEvent) => void>;

@Component({
  selector: 'rt-report-chart',
  imports: [ResizeObserverEntryDirective],
  templateUrl: './chart.component.html',
  styleUrl: './chart.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportChartComponent {
  public static deviceDpiChange$ = fromEvent(matchMedia('(resolution: 1dppx)'), 'change').pipe(
    debounceTime(100),
    observeOn(animationFrameScheduler),
    share(),
  );

  public chart?: ECharts;

  public echartsEventBindings = input<ReportsChartEventBindings>({});
  public options = input.required<ReportsEChartsOption | null>();
  public renderer = input<'svg' | 'canvas'>('canvas');
  // If you want the chart to redraw, change it's checksum
  public chartChecksum = input<string>('');

  public rendered = output<void>();
  public created = output<ECharts>();
  public resized = output<ECharts>();

  public size$ = new ReplaySubject<{ width: number; height: number }>(1);
  public chartOutlet = viewChild.required<ElementRef<HTMLDivElement>>('chartOutlet');

  private untilDestroyed = injectUntilDestroyed();
  private scheduledPaint?: number;
  private isConfigReady$ = toObservable(this.options).pipe(map(x => !!x));
  private theme$ = injectReportsChartTheme();
  private theme = toSignal(this.theme$);
  private injector = inject(Injector);
  private locale = inject(LOCALE_ID);
  private windowRef = inject(WindowRef);
  private momentConfigService = inject(ChartMomentConfigService);

  constructor() {
    this.momentConfigService.init();
    inject(DestroyRef).onDestroy(() => this.disposeChart());
  }

  public ngOnInit() {
    const deviceDpiChanged$ = ReportChartComponent.deviceDpiChange$.pipe();

    merge(this.size$.pipe(skip(1)), deviceDpiChanged$)
      .pipe(this.untilDestroyed)
      .subscribe(() => this.resizeChart());
  }

  public ngAfterViewInit() {
    const containerReady$ = this.size$.pipe(map(({ width, height }) => width > 0 && height > 0));

    combineLatest([this.isConfigReady$, containerReady$])
      .pipe(
        filter(([x, y]) => x && y),
        take(1),
      )
      .subscribe(() => {
        this.createChart();
        this.updateChart();

        runInInjectionContext(this.injector, () => {
          this.listenToChanges();
        });
      });
  }

  public ngOnDestroy() {
    this.forceHideTooltip();
  }

  public forceHideTooltip() {
    if (this.chart) this.chart.dispatchAction({ type: 'hideTip' });
  }

  private listenToChanges() {
    const optionsChanged$ = toObservable(this.options).pipe(
      distinctUntilChanged(),
      map(() => <const>'update'),
    );

    const themeChanged$ = this.theme$.pipe(
      skip(1),
      map(() => <const>'restart'),
    );

    const checksumChanged$ = toObservable(this.chartChecksum).pipe(
      distinctUntilChanged(),
      map(() => <const>'update'),
    );

    const optionsReset$ = toObservable(this.options).pipe(
      pairwise(),
      filter(([prev, next]) => prev === null && next !== null),
      map(() => <const>'restart'),
    );

    const source$ = merge(optionsChanged$, themeChanged$, checksumChanged$, optionsReset$).pipe(
      shareReplay(),
    );

    source$
      .pipe(
        // get together all values emitted in 1ms window,
        // most likely when all inputs changed at once
        buffer(source$.pipe(debounceTime(1, animationFrameScheduler))),
        map(actions =>
          actions.reduce((acc, action) => acc.add(action), new Set<'update' | 'restart'>()),
        ),
        filter(actions => actions.size > 0),
        this.untilDestroyed,
      )
      .subscribe(action => {
        if (action.has('restart')) {
          this.disposeChart();
          this.createChart();
        }
        this.schedulePaint(() => this.updateChart());
      });
  }

  private resizeChart() {
    if (!this.chart) {
      return;
    }

    this.chart.resize({
      silent: true,
    });
    this.resized.emit(this.chart);
  }

  private createChart() {
    this.chart = init(this.chartOutlet().nativeElement, getChartThemeInitOptions(this.theme()!), {
      devicePixelRatio: this.windowRef.nativeWindow.devicePixelRatio,
      locale: this.locale,
      renderer: this.renderer(),
    });
    this.created.emit(this.chart);
    Object.entries(this.echartsEventBindings()).forEach(([eventName, handler]) => {
      this.chart?.on(eventName, handler);
    });
  }

  private updateChart() {
    const chart = this.chart;
    if (!chart) {
      return;
    }
    chart.clear();

    const options = this.options() ?? {};

    chart.setOption(options);

    if (!options.tooltip?.position) {
      chart.setOption({
        tooltip: {
          position: getDefaultTooltipPosition(this.chartOutlet().nativeElement),
        },
      });
    }

    const themeUpdate = getChartThemeStylingOptions(
      <EChartsOption>chart.getOption(),
      this.theme()!,
    );
    chart.setOption(themeUpdate, false, false);

    chart.on('finished', this.onFinished);
  }

  private disposeChart() {
    this.forceHideTooltip();
    if (!this.chart) {
      return;
    }
    this.chart.off('finished', this.onFinished);
    Object.entries(this.echartsEventBindings()).forEach(([eventName, handler]) => {
      this.chart?.off(eventName, handler);
    });
    this.chart.dispose();
    this.chart = undefined;
  }

  private onFinished = () => {
    this.rendered.emit();
  };

  private schedulePaint(callback: FrameRequestCallback) {
    if (this.scheduledPaint) {
      cancelAnimationFrame(this.scheduledPaint);
    }

    this.scheduledPaint = requestAnimationFrame(callback);
  }
}
